import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Container } from '@/components/layout/Container'
import { AccountSidebar } from '@/components/account/AccountSidebar'
import { SignedInOnly } from '@/components/account/SignedInOnly'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { getMyOrders, cancelOrder, type OrderSummary } from '@/lib/api'
import { cn, formatPrice } from '@/lib/utils'

function orderStateLabel(o: OrderSummary): { label: string; tone: 'default' | 'active' | 'done' | 'muted' } {
  if (o.cancelled_at) return { label: 'Cancelled', tone: 'muted' }
  if (o.status === 'delivered') return { label: 'Delivered', tone: 'done' }
  if (o.shipping_status === 'in_transit' || o.shipping_status === 'exception') {
    return { label: o.shipping_status === 'exception' ? 'Delivery exception' : 'Shipped', tone: 'active' }
  }
  if (o.status === 'processing') return { label: 'Processing', tone: 'active' }
  if (['pending', 'initialized', 'authorized'].includes(o.payment_status)) return { label: 'Awaiting payment', tone: 'muted' }
  return { label: 'Paid', tone: 'default' }
}

const toneClass = {
  default: 'bg-ink/5 text-carbon',
  active: 'bg-wd-light-grey text-carbon',
  done: 'bg-wd-light-grey text-carbon',
  muted: 'bg-ink/5 text-carbon/50',
}

function formatDate(ms: number): string {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(ms)
}

export function AccountOrdersPage() {
  const { refreshUser } = useAuth()
  const [orders, setOrders] = useState<OrderSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setOrders(await getMyOrders())
    } catch {
      setError('Could not load your orders. Please try again.')
    }
  }, [])

  useEffect(() => {
    refreshUser()
    load()
  }, [load, refreshUser])

  const handleCancel = async (reference: string) => {
    if (!window.confirm(`Cancel order ${reference}?`)) return
    setCancelling(reference)
    const result = await cancelOrder(reference)
    setCancelling(null)
    if (result.success) {
      load()
    } else {
      setError('Could not cancel that order. Please try again.')
    }
  }

  return (
    <div className="pt-28 pb-24">
      <Container className="max-w-6xl">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
          <AccountSidebar activePath="/account/orders" />

          <div>
            <SignedInOnly>
              <p className="mb-2 text-xs uppercase tracking-[0.2em] text-wd-orange">orders</p>
            <h1 className="font-display text-4xl normal-case text-carbon md:text-5xl">Orders and Returns</h1>

            <div className="mt-10">
              {error && <p className="mb-6 text-sm text-carbon">{error}</p>}

              {orders === null ? (
                <p className="text-sm text-carbon/60">Loading your orders...</p>
              ) : orders.length === 0 ? (
                <div className="border border-ink/10 p-8">
                  <p className="text-sm text-carbon/70">You haven&apos;t placed any orders yet.</p>
                  <Link
                    to="/shop"
                    className="mt-4 inline-block text-xs font-medium uppercase tracking-[0.12em] text-carbon hover:underline"
                  >
                    Start shopping
                  </Link>
                </div>
              ) : (
                <ul className="divide-y divide-ink/10 border-y border-ink/10">
                  {orders.map((o) => {
                    const state = orderStateLabel(o)
                    return (
                      <li key={o.reference} className="flex flex-col gap-3 py-5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex min-w-0 items-center gap-6">
                          <div className="min-w-0">
                            <Link
                              to={`/order-status?reference=${o.reference}`}
                              className="text-sm font-medium text-carbon hover:text-wd-orange"
                            >
                              {o.reference}
                            </Link>
                            <p className="mt-0.5 text-xs text-carbon/50">
                              {formatDate(o.created_at)} · {o.item_count} item{o.item_count === 1 ? '' : 's'}
                            </p>
                          </div>
                          <span
                            className={cn(
                              'inline-flex shrink-0 items-center px-2 py-1 text-xs font-semibold uppercase tracking-[0.12em]',
                              toneClass[state.tone]
                            )}
                          >
                            {state.label}
                          </span>
                        </div>

                        <div className="flex items-center justify-between gap-4 sm:justify-end">
                          <span className="text-sm font-medium text-carbon">{formatPrice(o.total)}</span>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/order-status?reference=${o.reference}`}
                              className="text-xs font-medium uppercase tracking-[0.12em] text-carbon hover:underline"
                            >
                              View
                            </Link>
                            {o.can_cancel && (
                              <Button
                                variant="ghost"
                                onClick={() => handleCancel(o.reference)}
                                isLoading={cancelling === o.reference}
                                className="px-0 py-0 text-xs font-medium normal-case tracking-[0.12em] text-wd-orange hover:text-wd-orange"
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
            </SignedInOnly>
          </div>
        </div>
      </Container>
    </div>
  )
}